// client/components/HomeView.tsx
import { useState, useEffect } from 'react';
import { Item, api } from '../lib/api';
import { ShelfRow } from './ShelfRow';

interface Props {
    views: Item[];
    onNavigate: (item: Item) => void;
}

function isMusic(view: Item): boolean {
    return view.CollectionType === 'music';
}

export function HomeView({ views, onNavigate }: Props) {
    const [shelves, setShelves] = useState<Record<string, Item[]>>({});
    const [loading, setLoading] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (views.length === 0) return;

        // Fire all fetches in parallel
        views.forEach((view) => {
            setLoading((prev) => ({ ...prev, [view.Id]: true }));
            api.getLatest(view.Id, 20).then((items) => {
                setShelves((prev) => ({ ...prev, [view.Id]: items }));
                setLoading((prev) => ({ ...prev, [view.Id]: false }));
            });
        });
    }, [views]);

    return (
        <div className="animate-fade-in">
            {/* My Libraries banner row */}
            <div className="mb-10">
                <h2 className="text-base font-semibold text-gray-100 mb-3">My Libraries</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {views.map((view) => (
                        <LibraryCard key={view.Id} view={view} onNavigate={onNavigate} />
                    ))}
                </div>
            </div>

            {/* Per-library shelves */}
            {views.map((view) => {
                const items = shelves[view.Id] ?? [];
                const isLoadingShelf = loading[view.Id] ?? false;

                if (isLoadingShelf) {
                    return <ShelfSkeleton key={view.Id} title={`${view.Name} · Recently Added`} />;
                }

                if (items.length === 0) return null;

                return (
                    <ShelfRow
                        key={view.Id}
                        title={`${view.Name} · Recently Added`}
                        libraryItem={view}
                        items={items}
                        onNavigate={onNavigate}
                        square={isMusic(view)}
                    />
                );
            })}
        </div>
    );
}

// ── Library banner card ─────────────────────────────────────────────────────

function LibraryCard({ view, onNavigate }: { view: Item; onNavigate: (item: Item) => void }) {
    const [imgError, setImgError] = useState(false);
    const backdropUrl = api.getImageUrl(view.Id);

    return (
        <div
            className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group bg-surface"
            onClick={() => onNavigate(view)}
        >
            {!imgError ? (
                <img
                    src={backdropUrl}
                    alt={view.Name}
                    loading="lazy"
                    className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                    onError={() => setImgError(true)}
                />
            ) : (
                // Fallback gradient when no backdrop available
                <div className="w-full h-full bg-gradient-to-br from-surface to-black" />
            )}
            {/* Bottom gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            {/* Library name */}
            <span className="absolute bottom-2 left-3 text-sm font-bold text-white drop-shadow-md">
                {view.Name}
            </span>
        </div>
    );
}

// ── Skeleton while shelf is loading ────────────────────────────────────────

function ShelfSkeleton({ title }: { title: string }) {
    return (
        <div className="mb-8">
            <div className="text-base font-semibold text-gray-100 mb-3">{title}</div>
            <div className="flex gap-3 overflow-x-auto pb-2">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex-shrink-0 w-[150px]">
                        <div className="aspect-[2/3] rounded-md bg-surface animate-pulse" />
                        <div className="mt-1.5 h-3 w-3/4 rounded bg-surface animate-pulse" />
                    </div>
                ))}
            </div>
        </div>
    );
}
